import { ZONE_KINDS, type Camera, type CameraZone, type ZonePoint } from '@playplate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Undo2 } from 'lucide-react';
import { useRef, useState, type MouseEvent } from 'react';
import { EmptyState, ErrorState, Loading, PageHeader, Panel } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError } from '../lib/api';

/**
 * Zone configuration.
 *
 * Polygons are stored in normalised 0–1 coordinates, so calibration survives a
 * substream resolution change or a camera swap. The editor draws on a plain
 * 16:9 canvas rather than over a live frame — draw the rough shape here, then
 * check it against Live View and adjust. Overlaying the editor on the video
 * feed is the obvious next improvement.
 */
export function ZonesPage() {
  const { can } = useAuth();
  const [selectedCamera, setSelectedCamera] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const queryClient = useQueryClient();

  const camerasQuery = useQuery({
    queryKey: ['cameras', 'list'],
    queryFn: () => api.get<{ items: Camera[] }>('/cameras'),
  });

  const cameras = camerasQuery.data?.items ?? [];
  const cameraId = selectedCamera || cameras[0]?.id || '';

  const zonesQuery = useQuery({
    queryKey: ['zones', cameraId],
    queryFn: () => api.get<{ items: CameraZone[] }>(`/cameras/${cameraId}/zones`),
    enabled: Boolean(cameraId),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/zones/${id}`),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['zones'] }),
  });

  if (camerasQuery.isLoading) return <Loading label="Loading cameras" />;
  if (cameras.length === 0) {
    return (
      <div>
        <PageHeader title="Zones" />
        <EmptyState title="Add a camera first" message="Zones are drawn on a camera's field of view." />
      </div>
    );
  }

  const canWrite = can('zone:write');

  return (
    <div>
      <PageHeader
        title="Zones"
        subtitle="Zones drive occupancy, crowding and restricted-area rules."
        actions={
          <div className="flex items-center gap-2">
            <select
              className="input w-auto"
              value={cameraId}
              onChange={(e) => {
                setSelectedCamera(e.target.value);
                setCreating(false);
              }}
            >
              {cameras.map((camera) => (
                <option key={camera.id} value={camera.id}>
                  {camera.name}
                </option>
              ))}
            </select>
            {canWrite ? (
              <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                New zone
              </button>
            ) : null}
          </div>
        }
      />

      {creating ? <ZoneEditor cameraId={cameraId} onClose={() => setCreating(false)} /> : null}

      {zonesQuery.isLoading ? (
        <Loading label="Loading zones" />
      ) : zonesQuery.isError ? (
        <ErrorState message={(zonesQuery.error as Error).message} onRetry={() => void zonesQuery.refetch()} />
      ) : zonesQuery.data!.items.length === 0 ? (
        <EmptyState
          title="No zones on this camera"
          message="Without a zone, this camera reports frames but no occupancy."
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {zonesQuery.data!.items.map((zone) => (
            <Panel key={zone.id} title={zone.name}>
              <div className="p-4">
                <PolygonPreview polygon={zone.polygon} restricted={zone.restricted} />
                <dl className="mt-3 space-y-1 text-xs">
                  <Row label="Kind" value={zone.kind.replace('_', '/')} />
                  <Row label="Capacity" value={zone.capacity === null ? 'not set' : String(zone.capacity)} />
                  <Row
                    label="Crowd threshold"
                    value={
                      zone.crowdThreshold === null
                        ? 'no alert'
                        : `${zone.crowdThreshold} for ${zone.crowdSustainSeconds}s`
                    }
                  />
                  <Row label="Restricted" value={zone.restricted ? 'yes — any presence alerts' : 'no'} />
                  <Row label="Enabled" value={zone.enabled ? 'yes' : 'no'} />
                </dl>

                {canWrite ? (
                  <button
                    type="button"
                    className="btn-ghost mt-3 w-full justify-center text-critical hover:bg-critical/10"
                    onClick={() => {
                      if (window.confirm(`Delete zone "${zone.name}"? Its history is kept.`)) {
                        remove.mutate(zone.id);
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                    Delete zone
                  </button>
                ) : null}
              </div>
            </Panel>
          ))}
        </div>
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

function PolygonPreview({ polygon, restricted }: { polygon: ZonePoint[]; restricted: boolean }) {
  const points = polygon.map((p) => `${p.x * 100},${p.y * 100}`).join(' ');
  return (
    <svg viewBox="0 0 100 56.25" className="w-full rounded-lg border border-line bg-ink" role="img" aria-label="Zone shape">
      <polygon
        points={points}
        fill={restricted ? 'rgba(255,77,109,.22)' : 'rgba(166,46,255,.22)'}
        stroke={restricted ? '#ff4d6d' : '#a62eff'}
        strokeWidth="0.6"
      />
    </svg>
  );
}

function ZoneEditor({ cameraId, onClose }: { cameraId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const svgRef = useRef<SVGSVGElement>(null);
  const [points, setPoints] = useState<ZonePoint[]>([]);
  const [name, setName] = useState('');
  const [kind, setKind] = useState<string>('DINING');
  const [capacity, setCapacity] = useState<string>('');
  const [crowdThreshold, setCrowdThreshold] = useState<string>('');
  const [crowdSustainSeconds, setCrowdSustainSeconds] = useState(60);
  const [restricted, setRestricted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post(`/cameras/${cameraId}/zones`, {
        name,
        kind,
        polygon: points,
        capacity: capacity === '' ? null : Number(capacity),
        crowdThreshold: crowdThreshold === '' ? null : Number(crowdThreshold),
        crowdSustainSeconds,
        restricted,
        enabled: true,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['zones'] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create the zone'),
  });

  const addPoint = (event: MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const y = Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    setPoints((current) => [...current, { x: Number(x.toFixed(4)), y: Number(y.toFixed(4)) }]);
  };

  const canSave = points.length >= 3 && name.trim().length > 0;

  return (
    <Panel title="New zone" className="mb-6">
      <div className="grid gap-4 p-4 lg:grid-cols-[1.4fr_1fr]">
        <div>
          <svg
            ref={svgRef}
            viewBox="0 0 100 56.25"
            className="w-full cursor-crosshair rounded-lg border border-line bg-ink"
            onClick={addPoint}
            role="application"
            aria-label="Zone drawing canvas. Click to add points."
          >
            {Array.from({ length: 9 }, (_, i) => (
              <line
                key={`v${i}`}
                x1={(i + 1) * 10}
                y1="0"
                x2={(i + 1) * 10}
                y2="56.25"
                stroke="rgba(219,181,255,.08)"
                strokeWidth="0.2"
              />
            ))}
            {Array.from({ length: 5 }, (_, i) => (
              <line
                key={`h${i}`}
                x1="0"
                y1={(i + 1) * 9.375}
                x2="100"
                y2={(i + 1) * 9.375}
                stroke="rgba(219,181,255,.08)"
                strokeWidth="0.2"
              />
            ))}

            {points.length >= 2 ? (
              <polygon
                points={points.map((p) => `${p.x * 100},${p.y * 56.25}`).join(' ')}
                fill={restricted ? 'rgba(255,77,109,.2)' : 'rgba(166,46,255,.2)'}
                stroke={restricted ? '#ff4d6d' : '#a62eff'}
                strokeWidth="0.5"
              />
            ) : null}

            {points.map((point, index) => (
              <circle
                key={index}
                cx={point.x * 100}
                cy={point.y * 56.25}
                r="1"
                fill="#c8ff1a"
              />
            ))}
          </svg>

          <div className="mt-2 flex items-center justify-between text-xs text-muted">
            <span>
              {points.length === 0
                ? 'Click to place the first corner. Three points minimum.'
                : `${points.length} point${points.length === 1 ? '' : 's'} placed`}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                className="btn-ghost px-2 py-1"
                onClick={() => setPoints((p) => p.slice(0, -1))}
                disabled={points.length === 0}
              >
                <Undo2 className="h-3.5 w-3.5" aria-hidden />
                Undo
              </button>
              <button type="button" className="btn-ghost px-2 py-1" onClick={() => setPoints([])}>
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <label className="block">
            <span className="label mb-1.5 block">Zone name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>

          <label className="block">
            <span className="label mb-1.5 block">Kind</span>
            <select className="input" value={kind} onChange={(e) => setKind(e.target.value)}>
              {ZONE_KINDS.map((value) => (
                <option key={value} value={value}>
                  {value.replace('_', '/')}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="label mb-1.5 block">Capacity (optional)</span>
            <input
              type="number"
              className="input"
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
              placeholder="Seats or stations"
            />
            <span className="mt-1 block text-xs text-muted">
              A declared capacity raises the confidence recorded against sessions from this zone.
            </span>
          </label>

          <label className="block">
            <span className="label mb-1.5 block">Crowd threshold (optional)</span>
            <input
              type="number"
              className="input"
              value={crowdThreshold}
              onChange={(e) => setCrowdThreshold(e.target.value)}
              placeholder="People before alerting"
            />
          </label>

          <label className="block">
            <span className="label mb-1.5 block">Sustain seconds</span>
            <input
              type="number"
              className="input"
              value={crowdSustainSeconds}
              onChange={(e) => setCrowdSustainSeconds(Number(e.target.value))}
            />
            <span className="mt-1 block text-xs text-muted">
              How long the threshold must hold before an alert. Stops a queue that clears in ten
              seconds from paging anyone.
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 accent-lime"
              checked={restricted}
              onChange={(e) => setRestricted(e.target.checked)}
            />
            <span>
              Restricted area
              <span className="block text-xs text-muted">
                Any presence raises a high-severity alert.
              </span>
            </span>
          </label>

          {error ? (
            <p className="rounded-lg border border-critical/40 bg-critical/10 px-3 py-2 text-sm text-critical">
              {error}
            </p>
          ) : null}

          <div className="flex gap-2 pt-1">
            <button
              type="button"
              className="btn-primary"
              disabled={!canSave || create.isPending}
              onClick={() => create.mutate()}
            >
              {create.isPending ? 'Saving…' : 'Create zone'}
            </button>
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Panel>
  );
}
